import {
	addLabels,
	addListener,
	exclude,
	formatTimeblock,
	groupBy,
	officeHoursToBlocks,
	parseTimeblock,
	timeblockGroupsAdapter,
} from "lib/timeblocks";
import { pathOr } from "lib/functions";
import {
	removeTemporaryTickets,
	reservationsFromLocalStorage,
	setTicketLocalStorage,
} from "io/localStorage";
import { selectActiveUserReservations } from "state/reservations/selectors";
import { selectLine } from "state/lines/selectors";
import {
	selectLineReservations,
	selectOffice,
	selectOfficeHours,
} from "state/offices/selectors";
import { selectMyReservationsV1 } from "state/reservations/selectors";
import { selectTimeblocks } from "state/reservations/selectors";
import { useLinesById } from "state/lines/hooks";
import { useOffices } from "state/offices/hooks";
import { useState } from "react";
import React, { useDispatch, useEffect, useGlobal } from "reactn";

export const useTimeBlocks = (lineSlug, officeSlug, day, v2) => {
	const requestUnavailableDays = useDispatch("requestUnavailableDays");
	const getTimeBlocks = useDispatch("getTimeBlocks");
	const getTimeBlocksV3 = useDispatch("getTimeBlocksV3");
	const [offices] = useGlobal("officesv2");
	const [timeblocks] = useGlobal("timeblocks");
	const office = selectOffice(offices, officeSlug);
	const isAllowRervationsV3 = pathOr(
		false,
		["options", "isAllowRervationsV3"],
		office
	);

	useEffect(() => {
		if (v2 === null || !v2) return;
		if (isAllowRervationsV3) {
			getTimeBlocksV3({ lineSlug, officeSlug, date: new Date() });
		} else {
			requestUnavailableDays({ lineSlug, officeSlug });
			getTimeBlocks({ lineSlug, officeSlug, date: new Date() });
		}
	}, [
		office,
		requestUnavailableDays,
		getTimeBlocks,
		getTimeBlocksV3,
		v2,
		lineSlug,
		officeSlug,
		isAllowRervationsV3,
	]);

	const timeblocksV2 = selectTimeblocks(timeblocks, lineSlug, day);

	return [timeblocksV2, timeblocks.isLoading, timeblocks.error];
};

export const useTimeBlocksV1 = (lineSlug, officeSlug, day, v2) => {
	const getOfficeHours = useDispatch("getOfficeHours");
	const requestOfficeReservations = useDispatch("requestReservations");

	const [offices] = useGlobal("officesv2");
	const [lines] = useGlobal("linesv2");
	const [officeHours] = useGlobal("officeHours");
	const [reservations] = useGlobal("reservations");

	const office = selectOffice(offices, officeSlug);
	const line = selectLine(lines, lineSlug);
	const tz = office && office.timezone;

	const selectedOfficeHours = selectOfficeHours(
		officeHours,
		officeSlug,
		day,
		tz
	);

	const thisLineReservations = selectLineReservations(
		reservations,
		line && line.id
	);

	useEffect(() => {
		if (v2 === null || v2 === true) return;
		getOfficeHours({ officeId: office.id, officeSlug });
		requestOfficeReservations({ officeId: office.id });
	}, [getOfficeHours, office, requestOfficeReservations, v2, officeSlug]);

	const timeblocksV1 = officeHoursToBlocks(selectedOfficeHours, day).map(
		exclude(thisLineReservations)
	);

	return [
		timeblocksV1,
		officeHours.isLoading || reservations.isLoading,
		officeHours.error,
	];
};

export const useBookReservation = (
	lineSlug,
	officeSlug,
	lineId,
	v2,
	formsPayload
) => {
	const [myReservations] = useGlobal("myReservations");
	const [offices] = useGlobal("officesv2");
	const enqueueReservationV3 = useDispatch("enqueueReservationV3");
	const enqueueReservation = useDispatch("enqueueReservation");
	const pickReserveSaga = useDispatch("pickReserveSaga");
	const finishReserveSaga = useDispatch("finishReserveSaga");
	const finishReservationSaga = useDispatch("finishReservationSaga");
	const { loading, errorMessage } = myReservations;
	const office = selectOffice(offices, officeSlug);

	const isAllowRervationsV3 = pathOr(
		false,
		["options", "isAllowRervationsV3"],
		office
	);
	// const [forms] = useGlobal("forms");
	// const meta = pathOr({}, ["payload"], forms);

	const bookReservation = (timeblock, meet) => {
		const { from, to } = timeblock;
		if (isAllowRervationsV3) {
			if (formsPayload) {
				enqueueReservationV3({
					lineSlug,
					officeSlug,
					from,
					to,
					meet: meet,
					meta: {
						...formsPayload,
					},
				});
				return;
			}
			enqueueReservationV3({
				lineSlug,
				officeSlug,
				from,
				to,
				meet: meet,
			});
			return;
		}
		if (v2) {
			if (formsPayload) {
				enqueueReservation({
					lineSlug,
					officeSlug,
					from,
					to,
					meet: meet,
					meta: formsPayload,
				});
				return;
			}
			enqueueReservation({
				lineSlug,
				officeSlug,
				from,
				to,
				meet: meet,
			});
			return;
		}
		if (formsPayload) {
			pickReserveSaga({
				lineSlug,
				officeSlug,
				_lineId: lineId,
				block: { from, to },
				meet,
				meta: formsPayload,
			});
		}
		pickReserveSaga({
			lineSlug,
			officeSlug,
			_lineId: lineId,
			block: { from, to },
			meet,
		});
	};

	useEffect(
		() => () => finishReserveSaga({ lineId }),
		[finishReserveSaga, lineId]
	);
	useEffect(() => () => finishReservationSaga({}), [finishReservationSaga]);

	return [bookReservation, loading, errorMessage];
};

export const useUnavailableDays = (lineSlug, officeSlug) => {
	const requestUnavailableDays = useDispatch("requestUnavailableDays");
	const [unavailableDays] = useGlobal("unavailableDays");
	const { isLoading, error, byLineId } = unavailableDays;
	const days = byLineId[lineSlug];
	const [offices] = useGlobal("officesv2");
	const office = selectOffice(offices, officeSlug);
	const isAllowRervationsV3 = pathOr(
		false,
		["options", "isAllowRervationsV3"],
		office
	);

	useEffect(() => {
		if (office && !isAllowRervationsV3)
			requestUnavailableDays({ officeSlug, lineSlug });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [office]);

	return [error, isLoading, days ? days : []];
};

export const useReservations = () => {
	const [myReservations] = useGlobal("myReservations");
	const reservationsV1 = selectMyReservationsV1(myReservations);

	const [lines, linesLoading, linesError] = useLinesById(
		reservationsV1.map((r) => r.line_id)
	);
	const [offices, officesLoading] = useOffices();
	const { loading, errorMessage } = myReservations;

	return [
		//todo: pipe this, sort by from DESC
		selectActiveUserReservations(myReservations)
			.map(addCode) // do this when fetching
			.map(addLineName(lines))
			.map(addOfficeName(offices)),
		loading || officesLoading || linesLoading,
		errorMessage + linesError,
	];
};

export const addLineName = (lines) => (reservation) => {
	const line = reservation.line_id && lines.byId[reservation.line_id];
	return {
		...reservation,
		office: reservation.office || (line && line.officeSlug),
		lineName: reservation.lineName || (line && line.name),
	};
};

export const addOfficeName = (offices) => (reservation) => {
	const office = reservation.office && offices[reservation.office];
	return {
		...reservation,
		officeName: reservation.officeName || (office && office.name),
		categoryId: (office && office.category_id) || 11,
	};
};

export const useCancelReservation = (
	reservationId,
	v2,
	isLoggedInTemporary,
	officeSlug
) => {
	const deleteReservationV1 = useDispatch("deleteReserve");
	const deleteReservationV2 = useDispatch("deleteReservation");
	const deleteReservationV3 = useDispatch("deleteReservationV3");
	const [myReservations] = useGlobal("myReservations");
	const { userReservations } = reservationsFromLocalStorage();
	const [offices] = useGlobal("officesv2");
	const office = selectOffice(offices, officeSlug);
	const isAllowRervationsV3 = pathOr(
		false,
		["options", "isAllowRervationsV3"],
		office
	);

	const cancelReservation = () => {
		if (isLoggedInTemporary) {
			removeTemporaryTickets();
			const filter = userReservations.filter(
				(el) => el._id !== reservationId
			);
			filter.map((re) => setTicketLocalStorage(re, "removed"));
		}
		if (isAllowRervationsV3) {
			deleteReservationV3({ reservationId });
			return;
		}
		if (v2) {
			deleteReservationV2({ reservationId });
			return;
		}
		deleteReservationV1({ reserveId: reservationId });
	};

	const loading = myReservations.morituri.some((m) => m === reservationId);

	return [cancelReservation, myReservations.loading]; //, loading, error];
};

const addCode = (reservation) => ({
	...reservation,
	code: "RV" + String(reservation.id).substr(-3),
});
